import {
  LitElement, html, nothing,
  type TemplateResult, type PropertyValues,
} from 'lit';
import styles from './styles.js';
import { property, query, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { live } from 'lit/directives/live.js';
import type {
  Placement,
  Size,
  SelectOption,
  DropdownChangeEventDetail,
  DropdownFilterEventDetail,
  DropdownClearEventDetail,
} from '../types.js';
import { emit } from '../utils/dom.utils.js';
import { PlacementController } from '../utils/placement.controller.js';
import { VirtualScrollController } from '../utils/virtual-scroll.controller.js';
import { FormAssociatedMixin }      from '../utils/form-associated.mixin.js';
import { FormValidationController } from '../utils/form-validation.controller.js';

export type DropdownSize      = Size;
export type DropdownVariant   = 'outlined' | 'filled';
export type DropdownFloatLabel = 'over' | 'in' | 'on';

/**
 * uwc-dropdown
 *
 * Full-featured select / dropdown component.
 *
 * Features:
 *  - Single and multiple selection
 *  - Client-side filter with customisable placeholder
 *  - Editable / combobox mode (type-in trigger)
 *  - Virtual scrolling for large option lists
 *  - Float label (over / in / on)
 *  - Size variants: small | medium | large
 *  - Visual variants: outlined | filled
 *  - Invalid / error state
 *  - Fluid (full-width) mode
 *  - Option groups (non-selectable headers)
 *  - Clear (×) button
 *  - Loading state with spinner
 *  - Header / footer panel slots
 *  - Custom `renderOption` property
 *  - Full keyboard navigation (↑↓ Home End Enter Escape)
 *  - Accessible: role="combobox", aria-expanded, role="listbox",
 *    role="option", aria-selected, aria-activedescendant
 *  - Chip display for multi-select
 *
 * @attr options              - SelectOption[] — the option model.
 * @attr value                - Currently selected value (single) or value[] (multiple).
 * @attr placeholder          - Trigger placeholder text.
 * @attr label                - Label text used with float-label.
 * @attr multiple             - Enable multi-select (chips UI).
 * @attr filter               - Show filter input inside panel.
 * @attr editable             - Allow typing directly in trigger (combobox mode).
 * @attr filter-placeholder   - Placeholder for the filter input. Default: "Search…"
 * @attr show-clear           - Show clear (×) button when a value is selected.
 * @attr loading              - Show spinner and disable interaction.
 * @attr disabled             - Disable the entire component.
 * @attr scroll-height        - Max panel height (px). Default: 220.
 * @attr virtual              - Enable virtual scrolling for large lists.
 * @attr item-size            - Row height in px for virtual scroll. Default: 38.
 * @attr auto-display-first   - Select first option automatically on mount.
 * @attr empty-message        - Message shown when filter yields no results.
 * @attr placement            - Panel placement. Default: bottom-start.
 * @attr offset               - Gap between trigger and panel (px). Default: 4.
 * @attr max-selected-labels  - Max chips shown before collapse label. Default: 3.
 * @attr float-label          - Float label mode: over | in | on.
 * @attr variant              - Visual variant: outlined (default) | filled.
 * @attr size                 - Size preset: small | medium | large.
 * @attr invalid              - Mark field as invalid.
 * @attr fluid                - Stretch to full container width.
 *
 * @property renderOption - `(opt: SelectOption, index: number) => TemplateResult`
 *   Custom row renderer. Replaces the built-in option markup.
 *
 * @fires uwc-change  — detail: { value, label, originalEvent }
 * @fires uwc-filter  — detail: { query, originalEvent }
 * @fires uwc-clear   — detail: { previousValue }
 * @fires uwc-focus
 * @fires uwc-blur
 * @fires uwc-show
 * @fires uwc-hide
 *
 * @slot header - Content above the options list inside the panel.
 * @slot footer - Content below the options list inside the panel.
 *
 * @csspart trigger
 * @csspart label
 * @csspart chip
 * @csspart clear-btn
 * @csspart caret
 * @csspart panel
 * @csspart filter-input
 * @csspart option
 * @csspart group-header
 * @csspart empty
 * @csspart loading-icon
 *
 * @cssprop --uwc-dd-trigger-bg
 * @cssprop --uwc-dd-trigger-min-width - Trigger min width. Default: 140px.
 * @cssprop --uwc-dd-trigger-border
 * @cssprop --uwc-dd-trigger-radius
 * @cssprop --uwc-dd-trigger-color
 * @cssprop --uwc-dd-trigger-placeholder-color
 * @cssprop --uwc-dd-trigger-focus-border
 * @cssprop --uwc-dd-trigger-open-border
 * @cssprop --uwc-dd-trigger-disabled-bg
 * @cssprop --uwc-dd-chip-bg
 * @cssprop --uwc-dd-chip-color
 * @cssprop --uwc-dd-panel-bg
 * @cssprop --uwc-dd-panel-border
 * @cssprop --uwc-dd-panel-radius
 * @cssprop --uwc-dd-panel-shadow
 * @cssprop --uwc-dd-option-hover-bg
 * @cssprop --uwc-dd-option-selected-bg
 * @cssprop --uwc-dd-option-selected-color
 * @cssprop --uwc-dd-option-disabled-color
 * @cssprop --uwc-dd-group-color
 * @cssprop --uwc-dd-z
 * @cssprop --uwc-dd-duration
 */
export class UwcDropdown extends FormAssociatedMixin(LitElement) {

  static override styles = [styles];

  private _validation = new FormValidationController(this);

  // ── Existing props ──────────────────────────────────────────────────────────

  @property({ type: Array })                                     options: SelectOption[]      = [];
  @property({ attribute: false })                                value: unknown               = null;
  @property({ type: String })                                    placement: Placement         = 'bottom-start';
  @property({ type: Number })                                    offset                       = 4;
  @property({ type: String })                                    placeholder                  = 'Select…';
  @property({ type: Boolean, reflect: true })                    multiple                     = false;
  @property({ type: Boolean })                                   filter                       = false;
  @property({ type: String, attribute: 'filter-placeholder' })   filterPlaceholder            = 'Search…';
  @property({ type: Boolean, attribute: 'show-clear' })          showClear                    = false;
  @property({ type: Boolean, reflect: true })                    loading                      = false;
  @property({ type: Boolean, reflect: true })                    disabled                     = false;
  @property({ type: Number, attribute: 'scroll-height' })        override scrollHeight        = 220;
  @property({ type: Boolean, attribute: 'auto-display-first' })  autoDisplayFirst             = false;
  @property({ type: String, attribute: 'empty-message' })        emptyMessage                 = 'No results found.';
  @property({ type: Number, attribute: 'max-selected-labels' })  maxSelectedLabels            = 3;

  // ── New props ──────────────────────────────────────────────────────────────

  /** Enable virtual scrolling for large option lists (uses VirtualScrollController). */
  @property({ type: Boolean })                                   virtual                      = false;
  /** Row height used by the virtual scroller (px). Default: 34. */
  @property({ type: Number, attribute: 'item-size' })            itemSize                     = 34;
  /** Combobox mode: typing in the trigger filters options directly. */
  @property({ type: Boolean })                                   editable                     = false;
  /** Label text shown as a floating label (requires float-label attribute). */
  @property({ type: String })                                    label                        = '';
  /** Float label behaviour: over | in | on. */
  @property({ type: String, attribute: 'float-label', reflect: true })
                                                                 floatLabel: DropdownFloatLabel | null = null;
  /** Visual variant: outlined (default) | filled. */
  @property({ type: String, reflect: true })                     variant: DropdownVariant     = 'outlined';
  /** Size preset. */
  @property({ type: String, reflect: true })                     size: DropdownSize           = 'medium';
  /** Require a selection. */
  @property({ type: Boolean, reflect: true })                    required                     = false;
  /** Show invalid / error styling. */
  @property({ type: Boolean, reflect: true })                    invalid                      = false;
  /** Stretch to full container width. */
  @property({ type: Boolean, reflect: true })                    fluid                        = false;

  /**
   * Custom option renderer. Receives `(option, absoluteIndex)` and returns a
   * TemplateResult that fills the option row content (icon, label, badge etc.).
   * The host `.option` wrapper element is still rendered by the component.
   */
  renderOption?: (opt: SelectOption, index: number) => TemplateResult;

  // ── Internal state ──────────────────────────────────────────────────────────

  @state() private _filterQuery  = '';
  @state() private _highlightIdx = -1;
  @state() private _focusWithin  = false;

  /** True only during keyboard navigation — gates _scrollToHighlighted() to
   *  prevent the hover→highlight→programmaticScroll→rangeChange→rerender loop. */
  private _keyboardNav = false;

  @query('#trigger-btn')     private _triggerBtn!:     HTMLElement;
  @query('#editable-input')  private _editableInput?:  HTMLInputElement;
  @query('#filter-input')    private _filterInput?:    HTMLInputElement;
  @query('#panel')           private _panel!:          HTMLElement;
  @query('.options-scroll')  private _optionsScroll?:  HTMLElement;

  private readonly _onDropdownKeydown = this._handleDropdownKeydown.bind(this);

  // ── Virtual scroll controller ───────────────────────────────────────────────
  // Always instantiated; only attached to a DOM element when virtual=true.

  private _vs = new VirtualScrollController<SelectOption>(this, {
    onRangeChange: () => { /* host.requestUpdate() is called by the controller */ },
  });

  // ── Placement controller ────────────────────────────────────────────────────

  private _pc = new PlacementController(this, {
    getPanelEl: () => this._panel,

    onTriggerFound: (el) => {
      const onClick = (e: Event): void => {
        e.stopPropagation();
        // In editable mode the <input> handles open/close; only toggle for
        // clicks on the wrapper itself (actions area, caret).
        if (this.editable && (e.target as HTMLElement).closest('#editable-input')) return;
        this._pc.toggle();
      };
      const onKeydown = (e: KeyboardEvent): void => {
        if ((e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') && !this._pc.open) {
          e.preventDefault(); this._pc.show();
        }
      };
      el.setAttribute('aria-haspopup', 'listbox');
      el.addEventListener('click',   onClick);
      el.addEventListener('keydown', onKeydown as EventListener);
      return () => {
        el.removeEventListener('click',   onClick);
        el.removeEventListener('keydown', onKeydown as EventListener);
        el.removeAttribute('aria-haspopup');
      };
    },

    dismissOnOutsideClick: true,
    dismissOnEscape:       true,

    afterShow: () => {
      this._filterQuery  = '';
      this._highlightIdx = this._indexOfSelectedValue();
      document.addEventListener('keydown', this._onDropdownKeydown);
      emit(this, 'uwc-show');

      requestAnimationFrame(() => {
        if (this.editable) {
          this._editableInput?.focus();
          this._editableInput?.select();
        } else if (this.filter) {
          this._filterInput?.focus();
        }
        this._scrollToHighlighted();

        // Attach virtual scroll after the panel has layout (clientHeight > 0).
        if (this.virtual && this._optionsScroll) {
          this._vs.attach(this._optionsScroll);
          this._vs.setItems(this._filteredOptions());
        }
      });
    },

    afterHide: () => {
      document.removeEventListener('keydown', this._onDropdownKeydown);
      this._highlightIdx = -1;
      emit(this, 'uwc-hide');
      // Restore editable input to show the selected label.
      if (this.editable) this.requestUpdate();
      if (!this.editable) (this._triggerBtn as HTMLElement)?.focus();
    },

    afterPosition: ({ triggerRect, panelEl }) => {
      panelEl.style.minWidth = `${triggerRect.width}px`;
    },
  });

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  override firstUpdated(): void {
    this._pc.setTriggerElement(this._triggerBtn);

    if (this.autoDisplayFirst && !this._hasValue() && this.options.length) {
      const first = this.options.find(o => !o.group && !o.disabled);
      if (first) this._selectOption(first, undefined, { silent: true });
    }

    this._validation.syncFormValue(this._hasValue() ? JSON.stringify(this.value) : null);
    this._validation.revalidate(this._hasValue() ? 'v' : '', { required: this.required });
    this.addEventListener('uwc-blur', () => this._validation.setTouched(true));
  }

  protected override updated(changed: PropertyValues): void {
    super.updated(changed);
    this._pc.updated(changed as Map<PropertyKey, unknown>, null, null);

    if (changed.has('_highlightIdx') && this._keyboardNav) {
      this._scrollToHighlighted();
      this._keyboardNav = false;
    }

    // Keep virtual scroll items in sync while the panel is open.
    if (this.virtual && this._pc.open && this._optionsScroll) {
      if (changed.has('options') || changed.has('_filterQuery')) {
        this._vs.setItems(this._filteredOptions());
      }
    }
  }

  // ── Value helpers ──────────────────────────────────────────────────────────

  private _hasValue(): boolean {
    if (this.multiple) return Array.isArray(this.value) && (this.value as unknown[]).length > 0;
    return this.value !== null && this.value !== undefined;
  }

  private _isSelected(opt: SelectOption): boolean {
    if (this.multiple) return Array.isArray(this.value) && (this.value as unknown[]).includes(opt.value);
    return this.value === opt.value;
  }

  private _indexOfSelectedValue(): number {
    if (!this._hasValue()) return -1;
    if (this.multiple) return 0;
    return this._selectableOptions().findIndex(o => o.value === this.value);
  }

  private _labelForValue(val: unknown): string {
    return this.options.find(o => o.value === val)?.label ?? String(val);
  }

  private _triggerLabel(): string {
    if (!this._hasValue()) return '';
    if (this.multiple) {
      const selected = (this.value as unknown[]).map(v => this._labelForValue(v));
      if (selected.length <= this.maxSelectedLabels) return selected.join(', ');
      return `${selected.length} items selected`;
    }
    return this._labelForValue(this.value);
  }

  // ── Options helpers ────────────────────────────────────────────────────────

  private _filteredOptions(): SelectOption[] {
    if (!this._filterQuery) return this.options;
    const q = this._filterQuery.toLowerCase();
    return this.options.filter(o =>
      o.group !== undefined || o.label.toLowerCase().includes(q),
    );
  }

  private _selectableOptions(): SelectOption[] {
    return this._filteredOptions().filter(o => !o.group && !o.disabled);
  }

  // ── Selection ──────────────────────────────────────────────────────────────

  private _selectOption(
    opt: SelectOption,
    e?: Event,
    { silent = false } = {},
  ): void {
    if (opt.disabled || opt.group) return;

    let newValue: unknown;

    if (this.multiple) {
      const current = Array.isArray(this.value) ? [...(this.value as unknown[])] : [];
      const idx     = current.indexOf(opt.value);
      if (idx >= 0) current.splice(idx, 1);
      else          current.push(opt.value);
      newValue = current;
    } else {
      newValue = opt.value;
      this._pc.hide();
    }

    this.value = newValue;
    this._syncValidation();

    if (!silent) {
      emit<DropdownChangeEventDetail>(this, 'uwc-change', {
        value:         newValue,
        label:         opt.label,
        originalEvent: e,
      });
    }

    this.requestUpdate();
  }

  private _removeChip(val: unknown, e: Event): void {
    e.stopPropagation();
    if (!this.multiple || !Array.isArray(this.value)) return;
    this.value = (this.value as unknown[]).filter(v => v !== val);
    this._syncValidation();
    emit<DropdownChangeEventDetail>(this, 'uwc-change', {
      value: this.value,
      label: this._labelForValue(val),
    });
  }

  private _clear(e: Event): void {
    e.stopPropagation();
    const prev = this.value;
    this.value = this.multiple ? [] : null;
    this._syncValidation();
    emit<DropdownClearEventDetail>(this, 'uwc-clear', { previousValue: prev });
    emit<DropdownChangeEventDetail>(this, 'uwc-change', { value: this.value, label: '' });
  }

  private _syncValidation(): void {
    const hasVal = this._hasValue();
    this._validation.handleChange(hasVal ? 'v' : '', { required: this.required });
    this._validation.syncFormValue(hasVal ? JSON.stringify(this.value) : null);
  }

  // ── Filter ─────────────────────────────────────────────────────────────────

  private _handleFilterInput(e: InputEvent): void {
    this._filterQuery  = (e.target as HTMLInputElement).value;
    this._highlightIdx = 0;
    emit<DropdownFilterEventDetail>(this, 'uwc-filter', { query: this._filterQuery, originalEvent: e });
  }

  private _handleEditableInput(e: InputEvent): void {
    const val = (e.target as HTMLInputElement).value;
    this._filterQuery  = val;
    this._highlightIdx = 0;
    if (!this._pc.open) this._pc.show();
    emit<DropdownFilterEventDetail>(this, 'uwc-filter', { query: val, originalEvent: e });
  }

  // ── Keyboard navigation ────────────────────────────────────────────────────

  private _handleDropdownKeydown(e: KeyboardEvent): void {
    const opts = this._selectableOptions();
    if (!opts.length) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this._keyboardNav  = true;
        this._highlightIdx = (this._highlightIdx + 1) % opts.length;
        break;
      case 'ArrowUp':
        e.preventDefault();
        this._keyboardNav  = true;
        this._highlightIdx = (this._highlightIdx - 1 + opts.length) % opts.length;
        break;
      case 'Home':
        e.preventDefault();
        this._keyboardNav  = true;
        this._highlightIdx = 0;
        break;
      case 'End':
        e.preventDefault();
        this._keyboardNav  = true;
        this._highlightIdx = opts.length - 1;
        break;
      case 'Enter':
      case ' ':
        if (e.key === ' ' && (this.filter || this.editable)) break;
        e.preventDefault();
        if (this._highlightIdx >= 0 && opts[this._highlightIdx]) {
          this._selectOption(opts[this._highlightIdx], e);
        }
        break;
      case 'Tab':
        this._pc.hide();
        break;
    }
  }

  private _scrollToHighlighted(): void {
    requestAnimationFrame(() => {
      if (this.virtual) {
        if (this._highlightIdx < 0) return;
        // Map the selectable index → filtered options index for the VS controller.
        const targetOpt = this._selectableOptions()[this._highlightIdx];
        const filtIdx   = targetOpt ? this._filteredOptions().indexOf(targetOpt) : -1;
        if (filtIdx >= 0) this._vs.scrollToIndex(filtIdx, 'auto');
        return;
      }
      const item = this.shadowRoot?.querySelector<HTMLElement>('[data-highlighted="true"]');
      item?.scrollIntoView({ block: 'nearest' });
    });
  }

  // ── Render helpers ─────────────────────────────────────────────────────────

  private _renderChips(): TemplateResult {
    if (!this.multiple || !Array.isArray(this.value)) return html``;
    const visible  = (this.value as unknown[]).slice(0, this.maxSelectedLabels);
    const overflow = (this.value as unknown[]).length - visible.length;
    return html`
      <div class="chips-wrapper">
        ${visible.map(val => html`
          <span part="chip" class="chip">
            <span class="chip-label">${this._labelForValue(val)}</span>
            <button
              type="button"
              class="chip-remove"
              aria-label="Remove ${this._labelForValue(val)}"
              tabindex="-1"
              @click=${(e: Event) => this._removeChip(val, e)}
            ><uwc-icon name="close" size="1em"></uwc-icon></button>
          </span>`)}
        ${overflow > 0 ? html`<span class="chip chip--overflow">+${overflow}</span>` : nothing}
      </div>`;
  }

  /** Renders the option row content (icon / label / badge / check). */
  private _renderOptionContent(opt: SelectOption, isSelected: boolean): TemplateResult {
    return html`
      ${opt.icon ? html`<span class="opt-icon">${opt.icon}</span>` : nothing}
      <span class="opt-label">${opt.label}</span>
      ${opt.badge ? html`<span class="opt-badge">${opt.badge}</span>` : nothing}
      ${isSelected
        ? html`<span class="opt-check" aria-hidden="true">
            <uwc-icon name="check" size="1em"></uwc-icon>
          </span>`
        : nothing}
    `;
  }

  /** Standard (non-virtual) options list. */
  private _renderOptionsStandard(): TemplateResult {
    const filtered   = this._filteredOptions();
    const selectable = this._selectableOptions();

    if (!filtered.length) {
      return html`<div part="empty" class="empty">${this.emptyMessage}</div>`;
    }

    return html`
      <ul
        id="options-list"
        role="listbox"
        aria-multiselectable=${this.multiple}
        class="options-list"
      >
        ${repeat(
          filtered,
          (opt) => String(opt.value ?? opt.group ?? opt.label),
          (opt, absIdx) => {
            if (opt.group !== undefined) {
              return html`
                <li part="group-header" class="group-header" role="presentation">
                  ${opt.icon ? html`<span class="opt-icon">${opt.icon}</span>` : nothing}
                  <span>${opt.group || opt.label}</span>
                </li>`;
            }

            const selIdx    = selectable.indexOf(opt);
            const isHighlit = selIdx === this._highlightIdx;
            const isSel     = this._isSelected(opt);

            return html`
              <li
                part="option"
                role="option"
                id="opt-${String(opt.value)}"
                aria-selected=${isSel}
                aria-disabled=${opt.disabled ?? false}
                data-highlighted=${isHighlit}
                class=${classMap({
                  'option':              true,
                  'option--selected':    isSel,
                  'option--highlighted': isHighlit,
                  'option--disabled':    !!opt.disabled,
                })}
                title=${opt.title ?? ''}
                @click=${(e: Event) => { if (!opt.disabled) this._selectOption(opt, e); }}
                @mouseenter=${() => { if (!opt.disabled) this._highlightIdx = selIdx; }}
              >
                ${this.renderOption
                  ? this.renderOption(opt, absIdx)
                  : this._renderOptionContent(opt, isSel)}
              </li>`;
          },
        )}
      </ul>`;
  }

  /** Virtually-scrolled options list using VirtualScrollController. */
  private _renderOptionsVirtual(): TemplateResult {
    const filtered   = this._filteredOptions();
    const selectable = this._selectableOptions();

    if (!filtered.length) {
      return html`<div part="empty" class="empty">${this.emptyMessage}</div>`;
    }

    const h = `${this.scrollHeight}px`;

    return html`
      <div
        class="options-scroll"
        style=${styleMap({
          height:          h,
          overflowY:       'auto',
          overflowAnchor:  'none',
          position:        'relative',
        })}
        role="listbox"
        aria-multiselectable=${this.multiple}
      >
        <div
          style=${styleMap({ height: `${this._vs.paddingBefore}px` })}
          aria-hidden="true"
        ></div>

        ${this._vs.visibleItems.map(({ item: opt, index: absIdx }) => {
          if (opt.group !== undefined) {
            return html`
              <div
                part="group-header"
                class="group-header"
                role="presentation"
                style=${styleMap({ height: `${this.itemSize}px`, boxSizing: 'border-box' })}
              >
                ${opt.icon ? html`<span class="opt-icon">${opt.icon}</span>` : nothing}
                <span>${opt.group || opt.label}</span>
              </div>`;
          }

          const selIdx    = selectable.indexOf(opt);
          const isHighlit = selIdx === this._highlightIdx;
          const isSel     = this._isSelected(opt);

          return html`
            <div
              part="option"
              role="option"
              id="opt-${String(opt.value)}"
              aria-selected=${isSel}
              aria-disabled=${opt.disabled ?? false}
              data-highlighted=${isHighlit}
              class=${classMap({
                'option':              true,
                'option--selected':    isSel,
                'option--highlighted': isHighlit,
                'option--disabled':    !!opt.disabled,
              })}
              style=${styleMap({ height: `${this.itemSize}px`, boxSizing: 'border-box' })}
              title=${opt.title ?? ''}
              @click=${(e: Event) => { if (!opt.disabled) this._selectOption(opt, e); }}
              @mouseenter=${() => { if (!opt.disabled) this._highlightIdx = selIdx; }}
            >
              ${this.renderOption
                ? this.renderOption(opt, absIdx)
                : this._renderOptionContent(opt, isSel)}
            </div>`;
        })}

        <div
          style=${styleMap({ height: `${this._vs.paddingAfter}px` })}
          aria-hidden="true"
        ></div>
      </div>`;
  }

  private _renderFilterInput(): TemplateResult {
    return html`
      <div class="filter-wrapper">
        <uwc-icon name="search" size="1em" class="filter-icon"></uwc-icon>
        <input
          id="filter-input"
          part="filter-input"
          type="text"
          class="filter-input"
          placeholder=${this.filterPlaceholder}
          .value=${live(this._filterQuery)}
          autocomplete="off"
          @input=${this._handleFilterInput}
          @keydown=${(e: KeyboardEvent) => {
            if (['ArrowUp', 'ArrowDown'].includes(e.key)) e.preventDefault();
          }}
        />
      </div>`;
  }

  private _renderTriggerActions(open: boolean): TemplateResult {
    const showClearBtn = this.showClear && this._hasValue() && !this.disabled && !this.loading;
    return html`
      <span class="trigger-actions">
        ${showClearBtn ? html`
          <span
            part="clear-btn"
            class="clear-btn"
            role="button"
            tabindex="-1"
            aria-label="Clear"
            @click=${this._clear}
          >
            <uwc-icon name="close" size="1em"></uwc-icon>
          </span>` : nothing}

        ${this.loading
          ? html`<span part="loading-icon" class="loading-icon" aria-hidden="true"></span>`
          : html`
            <uwc-icon name="chevron-down" size="1em" class="caret ${open ? 'caret--open' : ''}"></uwc-icon>
          `}
      </span>`;
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  protected override render() {
    const open     = this._pc.open;
    const hasValue = this._hasValue();
    const label    = this._triggerLabel();

    // Float label is "active" (floated up) when value exists OR panel is open.
    const floatActive = hasValue || open;

    const triggerClasses = classMap({
      'trigger':          true,
      'trigger--open':    open,
      'trigger--loading': this.loading,
      'trigger--empty':   !hasValue,
      'trigger--has-value': hasValue,
      'trigger--editable': this.editable,
    });

    // ── Trigger element ──────────────────────────────────────────────────────
    // For editable mode, the trigger is a <div role="combobox"> wrapping an
    // <input>. For normal mode, it's the existing <button>.
    const triggerEl = this.editable
      ? html`
          <div
            id="trigger-btn"
            part="trigger"
            role="combobox"
            class=${triggerClasses}
            aria-expanded=${open}
            aria-controls="panel"
            aria-haspopup="listbox"
          >
            <input
              id="editable-input"
              class="editable-input"
              type="text"
              autocomplete="off"
              .value=${live(open ? this._filterQuery : label)}
              placeholder=${this.placeholder}
              ?disabled=${this.disabled || this.loading}
              aria-label=${label || this.placeholder}
              @input=${this._handleEditableInput}
              @focus=${() => {
                this._focusWithin = true;
                if (!this._pc.open) this._pc.show();
                emit(this, 'uwc-focus');
              }}
              @blur=${() => { this._focusWithin = false; emit(this, 'uwc-blur'); }}
              @keydown=${(e: KeyboardEvent) => {
                if ((e.key === 'ArrowDown' || e.key === 'Enter') && !this._pc.open) {
                  e.preventDefault(); this._pc.show();
                }
              }}
            />
            ${this._renderTriggerActions(open)}
          </div>`
      : html`
          <button
            id="trigger-btn"
            part="trigger"
            type="button"
            class=${triggerClasses}
            ?disabled=${this.disabled || this.loading}
            aria-expanded=${open}
            aria-controls="panel"
            aria-label=${label || this.placeholder}
            @focus=${() => { this._focusWithin = true;  emit(this, 'uwc-focus'); }}
            @blur=${()  => { this._focusWithin = false; emit(this, 'uwc-blur');  }}
          >
            ${this.multiple && hasValue
              ? this._renderChips()
              : html`<span part="label" class="trigger-label ${!hasValue ? 'is-placeholder' : ''}">
                       ${hasValue ? label : this.placeholder}
                     </span>`}
            ${this._renderTriggerActions(open)}
          </button>`;

    // ── Float label ──────────────────────────────────────────────────────────
    const floatLabelEl = this.floatLabel && this.label
      ? html`<label
               class=${classMap({
                 'float-label':        true,
                 'float-label--active': floatActive,
               })}
               aria-hidden="true"
             >${this.label}</label>`
      : nothing;

    // ── Panel slots ──────────────────────────────────────────────────────────
    const hasSlotHeader = !!this.querySelector('[slot="header"]');
    const hasSlotFooter = !!this.querySelector('[slot="footer"]');

    return html`
      <div class="dd-root">
        ${triggerEl}
        ${floatLabelEl}
      </div>

      <div
        id="panel"
        part="panel"
        popover="manual"
        class="panel ${open ? 'is-open' : ''}"
        data-placement=${this.placement}
        aria-label="Options"
      >
        ${hasSlotHeader
          ? html`<div class="panel-header"><slot name="header"></slot></div>`
          : nothing}

        ${this.filter && !this.editable ? this._renderFilterInput() : nothing}

        <div class="options-wrapper" style=${!this.virtual ? styleMap({ maxHeight: `${this.scrollHeight}px` }) : ''}>
          ${this.virtual ? this._renderOptionsVirtual() : this._renderOptionsStandard()}
        </div>

        ${hasSlotFooter
          ? html`<div class="panel-footer"><slot name="footer"></slot></div>`
          : nothing}
      </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-dropdown': UwcDropdown; }
}
